'use client'

import { FC, useState, useRef, useMemo, useEffect } from 'react'
import { focusInCurrentTarget } from 'lib/helpers'

import { Button } from './Button'

type ExpandMenuProps = {
  label: string
  expanded?: boolean
  children?: React.ReactNode
  className?: string
  updateExpanded?: (expanded: boolean) => void
  [key: string]: any
}

export const ExpandMenu: FC<ExpandMenuProps> = ({
  label,
  expanded,
  children,
  className,
  updateExpanded,
  ...rest
}) => {
  const [localExpanded, setLocalExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
  const widthRef = useRef<{ button: number | null; dropdown: number }>({
    button: null,
    dropdown: 0,
  })

  const isExpanded = useMemo(() => {
    if (typeof expanded === 'undefined') return localExpanded
    return expanded
  }, [expanded, localExpanded])

  const mouseLeave = () => {
    if (typeof expanded === 'undefined') setLocalExpanded(false)

    if (containerRef.current && !isExpanded) {
      containerRef.current.style.width = `${widthRef.current.button}px`
    }
  }

  const mouseEnter = () => {
    if (typeof expanded === 'undefined') setLocalExpanded(true)

    if (contentRef.current && buttonRef.current && containerRef.current) {
      widthRef.current.dropdown = contentRef.current.offsetWidth
      widthRef.current.button = buttonRef.current.offsetWidth + 4
      containerRef.current.style.width = `${widthRef.current.dropdown}px`
    }
  }

  const onFocus = (e) => {
    if (typeof expanded === 'undefined') return setLocalExpanded(true)

    return updateExpanded && updateExpanded(true)
  }

  const onBlur = (e) => {
    const focusInTarget = focusInCurrentTarget({
      relatedTarget: e.relatedTarget,
      currentTarget: e.currentTarget.parentNode,
    })
    if (focusInTarget) return null

    if (typeof expanded === 'undefined') return setLocalExpanded(false)

    return updateExpanded && updateExpanded(false)
  }

  useEffect(() => {
    if (contentRef.current && buttonRef.current && containerRef.current) {
      widthRef.current.dropdown = contentRef.current.offsetWidth
      widthRef.current.button = buttonRef.current.offsetWidth + 4
      containerRef.current.style.width = `${widthRef.current.button}px`
    }
  }, [])

  useEffect(() => {
    if (expanded) {
      mouseEnter()
    } else {
      mouseLeave()
    }
  }, [expanded])

  return (
    <div
      ref={containerRef}
      className={`relative ml-8 w-auto rounded-8 leading-[13px] ${className}`}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      style={{
        transition: 'width 0.4s cubic-bezier(.7, 0, .2, 1)',
      }}
      {...rest}
    >
      <div className="background-blur absolute inset-0 -z-[1] h-full w-full rounded-8" />
      <Button
        as="button"
        styled={false}
        aria-expanded={isExpanded}
        aria-controls={`${label}-expanded-menu`}
        ref={buttonRef}
        className={`ease h-full transition-opacity duration-300 ${
          isExpanded ? 'opacity-0' : 'opacity-100'
        }`}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {label}
      </Button>
      <div
        ref={contentRef}
        className={`ease absolute left-0 top-0 flex h-full whitespace-nowrap transition-opacity duration-300 ${
          isExpanded
            ? 'pointer-events-auto opacity-100 delay-150'
            : 'pointer-events-none opacity-0'
        }`}
        id={`${label}-expanded-menu`}
      >
        {children}
      </div>
    </div>
  )
}
